import { useState, useEffect } from 'react';
import { Pencil, Save, X } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@neurograph/primitives';
import type { BriefSection, BriefSectionKey } from '../types';

interface BriefSectionEditorProps {
  sectionKey: BriefSectionKey;
  label: string;
  data: BriefSection | undefined;
  onSave: (sectionKey: BriefSectionKey, value: BriefSection) => Promise<void>;
  isSaving?: boolean;
}

function renderFieldValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) return JSON.stringify(value, null, 2);
  return String(value ?? '');
}

export function BriefSectionEditor({
  sectionKey,
  label,
  data,
  onSave,
  isSaving,
}: BriefSectionEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    setDraft(
      typeof data === 'object' && data !== null
        ? JSON.stringify(data, null, 2)
        : String(data ?? ''),
    );
  }, [data]);

  const handleSave = async () => {
    try {
      const parsed = typeof data === 'object' ? JSON.parse(draft) : draft;
      await onSave(sectionKey, parsed);
      setEditing(false);
    } catch {
      // JSON parse error — stay in editing mode
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft(
      typeof data === 'object' && data !== null
        ? JSON.stringify(data, null, 2)
        : String(data ?? ''),
    );
  };

  const hasData = data !== undefined && data !== null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            {label}
          </span>
          {editing ? (
            <div className="flex gap-1.5">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
              >
                <Save size={12} />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1 px-2.5 py-1 text-xs text-neutral-500 border border-neutral-200 rounded-md hover:bg-neutral-50"
              >
                <X size={12} />
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 px-2.5 py-1 text-xs text-neutral-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            >
              <Pencil size={12} />
              {hasData ? 'Edit' : 'Add'}
            </button>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {editing ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full p-3 border-0 bg-neutral-50 rounded-lg font-mono text-sm resize-y outline-none focus:ring-2 focus:ring-primary-200 min-h-[100px]"
            rows={Math.max(4, draft.split('\n').length)}
          />
        ) : hasData ? (
          <div className="space-y-2">
            {typeof data === 'object' && data !== null ? (
              Object.entries(data).map(([key, value]) => (
                <div key={key} className="flex gap-3 text-sm">
                  <span className="font-medium text-neutral-500 min-w-[120px] capitalize">
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span className="text-neutral-800 leading-relaxed">
                    {renderFieldValue(value)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-neutral-700 leading-relaxed">
                {String(data)}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-neutral-400 italic">Not yet configured</p>
        )}
      </CardBody>
    </Card>
  );
}
